import React, { useState, useEffect, useCallback } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Box,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';

interface UserGroup {
    id: number;
    name: string;
    color?: string;
}

interface GroupSelectorProps {
    value: number | null;
    onChange: (groupId: number | null) => void;
    companyId?: number | null;
    disabled?: boolean;
}

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

export const GroupSelector: React.FC<GroupSelectorProps> = ({
    value,
    onChange,
    companyId,
    disabled = false,
}) => {
    const [groups, setGroups] = useState<UserGroup[]>([]);
    const [loading, setLoading] = useState(true);

    const getToken = () => localStorage.getItem('token');

    const loadGroups = useCallback(async () => {
        const token = getToken();
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const params = new URLSearchParams();
            if (companyId) {
                params.append('companyId', companyId.toString());
            }

            const response = await fetch(`${API_URL}/analytics/groups?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setGroups(data.groups || []);
            }
        } catch (err) {
            console.error('GroupSelector: Error loading groups:', err);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        setLoading(true);
        onChange(null);
        loadGroups();
    }, [loadGroups]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (groups.length === 0) {
        return null;
    }

    return (
        <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <GroupIcon fontSize="small" />
                    Gruppe
                </Box>
            </InputLabel>
            <Select
                value={value === null ? '' : value}
                label="Gruppe"
                onChange={(e) => {
                    const newValue = e.target.value as string | number;
                    onChange(newValue === '' ? null : Number(newValue));
                }}
            >
                <MenuItem value="">
                    <em>Alle Gruppen</em>
                </MenuItem>
                {groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {group.color && (
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: group.color,
                                    }}
                                />
                            )}
                            {group.name}
                        </Box>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default GroupSelector;
